import { Link, useNavigate } from "react-router-dom";
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import FormInput from "../../components/CustomComponents/Input/formInput";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const initialSupplierData = {
  S_Name: "",
  S_Description: "",
  S_Type: "",
  S_Email: "",
  S_Contact: "",
  S_Address1: "",
  S_Address2: "",
  S_City: "",
  S_Country: "",
  Post_Code: "",
  S_Note: "",
  Is_Active: true,
  S_DateTime: new Date(),
  CompID: 1,
};

function AddSupplier() {
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState(initialSupplierData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const initialFormSnapshotRef = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      S_Name: supplierData.S_Name || "",
      S_Description: supplierData.S_Description || "",
      S_Type: supplierData.S_Type || "",
      S_Email: supplierData.S_Email || "",
      S_Contact: supplierData.S_Contact || "",
      S_Address1: supplierData.S_Address1 || "",
      S_Address2: supplierData.S_Address2 || "",
      Post_Code: supplierData.Post_Code || "",
      S_Country: supplierData.S_Country || "",
      S_City: selectedCity || "",
      S_Note: supplierData.S_Note || "",
      Is_Active: Boolean(supplierData.Is_Active),
    });
  }, [supplierData, selectedCity]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleInputChange = useCallback((e, isDropdown = false) => {
    if (isDropdown) {
      const { name, value } = e;
      setSupplierData((prev) => ({
        ...prev,
        [name]: value || "",
      }));
    } else {
      const { name, value, type, checked } = e.target;
      setSupplierData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  }, []);

  const handleToggleChange = useCallback((checked, name) => {
    setSupplierData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const submitSupplier = async (submitData, exitAfterSave) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_ADD_SUPPLIER, submitData);

      if (response.data.status) {
        showSuccessToast(response.data.message || "Supplier Successfully Created");
        initialFormSnapshotRef.current = getFormSnapshot();
        setIsSubmitted(true);
        if (exitAfterSave) {
          navigate("/supplier/manageSupplier");
        }
        return true;
      }
      showErrorToast(response.data.message || "Error Creating Supplier");
      return false;
    } catch (err) {
      if (err.response) {
        showErrorToast(err.response.data.message || "Error submitting supplier");
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in supplier submission");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!supplierData.S_Name.trim()) {
      showErrorToast("Please provide a valid supplier name");
      return false;
    }

    if (!supplierData.S_Email || !validateEmail(supplierData.S_Email)) {
      showErrorToast("Please provide a valid email");
      return false;
    }

    if (supplierData.S_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return false;
    }

    const submitData = {
      ...supplierData,
      S_DateTime: new Date(),
      S_City: selectedCity,
    };

    submitData.CompID = CompID;
    return submitSupplier(submitData, exitAfterSave);
  };

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/supplier">Manage Suppliers</Link>
            </li>
            <li className="breadcrumb-item active">Add Supplier</li>
          </ol>
          <h1 className="page-header mb-0">Add Supplier</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/supplier/manageSupplier")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="240px">
      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <h5 className="mb-4 text-primary fw-semibold">Supplier Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <RequiredLabel text="Supplier Name" />
                <FormInput
                  name="S_Name"
                  placeholder="Enter Supplier Name"
                  type="text"
                  value={supplierData.S_Name}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <NormalLabel text="Supplier Type" />
                <FormInput
                  name="S_Type"
                  placeholder="Enter Supplier Type"
                  type="text"
                  value={supplierData.S_Type}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-12">
                <NormalLabel text="Description" />
                <FormInput
                  name="S_Description"
                  placeholder="Enter Description"
                  type="text"
                  value={supplierData.S_Description}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Contact Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <RequiredLabel text="Email" />
                <FormInput
                  name="S_Email"
                  placeholder="Enter Email"
                  type="email"
                  value={supplierData.S_Email}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <NormalLabel text="Contact Number" />
                <FormInput
                  name="S_Contact"
                  placeholder="Enter Contact Number"
                  type="text"
                  value={supplierData.S_Contact}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Address Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <NormalLabel text="Address Line 1" />
                <FormInput
                  name="S_Address1"
                  placeholder="Enter Address Line 1"
                  type="text"
                  value={supplierData.S_Address1}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-12">
                <NormalLabel text="Address Line 2" />
                <FormInput
                  name="S_Address2"
                  placeholder="Enter Address Line 2"
                  type="text"
                  value={supplierData.S_Address2}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <RequiredLabel text="Country / City" />
              <CountryCitySelector
                selectedCountry={supplierData.S_Country}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                onChange={(updatedData) => setSupplierData((prev) => ({ ...prev, ...updatedData }))}
                setLoading={setLoading}
                isDarkMode={isDarkMode}
                selectedValueIsLabel={true}
                countryFieldName="S_Country"
                cityFieldName="S_City"
              />
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <NormalLabel text="Post Code" />
                <FormInput
                  name="Post_Code"
                  placeholder="Enter Post Code"
                  type="text"
                  value={supplierData.Post_Code}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Additional Information</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <NormalLabel text="Notes" />
                <FormInput
                  name="S_Note"
                  placeholder="Enter Notes"
                  type="text"
                  value={supplierData.S_Note}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <h5 className="mb-4 text-primary fw-semibold">Status</h5>
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <ToggleSwitch
                  isChecked={supplierData.Is_Active}
                  onToggle={(checked) => handleToggleChange(checked, "Is_Active")}
                  name={"Is_Active"}
                  label={"Active"}
                  disabled={false}
                  themeColor={themeColor}
                />
              </div>
            </div>

          </PanelBody>
        </Panel>
      </div>
      </GridSectionLoader>
    </div>
  );
}

export default AddSupplier;